import { partnerSupplier } from '@/utils/environment';
import { SortOrder } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { useMemo, useState } from 'react';
import { useSalesPortalProspectFinderCategoryOptionsQuery } from '@/experiences/sales-portal/domain/prospects/blocs/documents/sales-portal-prospect-finder-category-options.generated';

export enum BusinessStatus {
  CURRENT = 'CURRENT',
  PROSPECT = 'PROSPECT',
}

export interface CategoryOption {
  label: string;
  value: string;
}

export interface ProspectFiltering {
  businessStatus: BusinessStatus | null;
  categories: string;
  categoryOptions: CategoryOption[];
  isCurrentRestaurant: boolean | undefined;
  price: string;
  selectedCategories: Set<string>;
  selectedPriceRanges: Set<string>;
  setBusinessStatus: (value: BusinessStatus | null) => void;
  setSelectedCategories: (value: Set<string>) => void;
  setSelectedPriceRanges: (value: Set<string>) => void;
  setSortOrder: (value: SortOrder) => void;
  sortOrder: SortOrder;
}

/**
 * Holds Prospect Finder filter state (status / category / price / sort)
 * plus the Yelp category options fetched for the dropdown. Returns the
 * derived query-ready filter values, the raw selected sets for UI state,
 * and setters for each axis.
 */
export const useProspectFinderFiltering = (): ProspectFiltering => {
  const [businessStatus, setBusinessStatus] = useState<BusinessStatus | null>(
    null,
  );
  const [selectedPriceRanges, setSelectedPriceRanges] = useState<Set<string>>(
    new Set(),
  );
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.BEST_MATCH);

  const { data: categoryData } =
    useSalesPortalProspectFinderCategoryOptionsQuery({
      variables: {
        businessOrganizationId: partnerSupplier.businessOrganizationUUID,
      },
    });

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    if (!categoryData?.getProspectFinderCategoryOptions) {
      return [];
    }
    return categoryData.getProspectFinderCategoryOptions.map((option) => ({
      label: option.display_name,
      value: option.yelp_alias,
    }));
  }, [categoryData]);

  const categories = Array.from(selectedCategories).join(',').toLowerCase();
  const price = Array.from(selectedPriceRanges).join(',');
  const isCurrentRestaurant =
    businessStatus === BusinessStatus.CURRENT
      ? true
      : businessStatus === BusinessStatus.PROSPECT
        ? false
        : undefined;

  return {
    businessStatus,
    categories,
    categoryOptions,
    isCurrentRestaurant,
    price,
    selectedCategories,
    selectedPriceRanges,
    setBusinessStatus,
    setSelectedCategories,
    setSelectedPriceRanges,
    setSortOrder,
    sortOrder,
  };
};
